import { Box, Typography } from '@mui/material';

// Use unified date utils
import { moment } from '../../utils/dateUtils';
import { isItemExpired } from '../../utils/proposalCalculations';

const fontFamily = '"Inter", sans-serif';

const ProposalViewInvestment = ({ 
  lineItems = [], 
  totalPay = 0, 
  rebates = 0, 
  instants = 0,
  discountsList = [],
  rebatesList = [],
  financingOptions,
  isSigned = false,
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const allRows = [];

  const selectedDiscounts = (discountsList || []).filter(d => d.isSelected);
  if (selectedDiscounts.length > 0) {
    selectedDiscounts.forEach(discount => {
      const expired = isItemExpired(discount, isSigned);
      allRows.push({
        description: discount.name || 'Discount',
        amount: `-${formatCurrency(discount.amount || 0)}`,
        expires: discount.expires ? moment(discount.expires).format('MM/DD/YYYY') : null,
        expired,
        isDiscount: true,
      });
    });
  } else if (instants > 0) {
    allRows.push({
      description: 'Instant Discounts',
      amount: `-${formatCurrency(instants)}`,
      expires: null,
      expired: false,
      isDiscount: true,
    });
  }

  const selectedRebates = (rebatesList || []).filter(r => r.isSelected);
  if (selectedRebates.length > 0) {
    selectedRebates.forEach(rebate => {
      const expired = isItemExpired(rebate, isSigned);
      allRows.push({
        description: rebate.name || 'Rebate',
        amount: `-${formatCurrency(rebate.amount || 0)}`,
        expires: rebate.expires ? moment(rebate.expires).format('MM/DD/YYYY') : null,
        expired,
        isRebate: true,
      });
    });
  } else if (rebates > 0) {
    allRows.push({
      description: 'Rebates',
      amount: `-${formatCurrency(rebates)}`,
      expires: null,
      expired: false,
      isRebate: true,
    });
  }

  // Check if we have discounts/rebates to show
  const hasDiscountsOrRebates = allRows.length > 0;

  // Hide entire section if no totalPay
  if (totalPay === 0) return null;

  return (
    <Box sx={{ mb: 4, px: 2 }}>
      {/* Investment Header and Discounts/Rebates - only if we have them */}
      {hasDiscountsOrRebates && (
        <>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 0,
            }}
          >
            <Box
              sx={{
                backgroundColor: '#0d3c61',
                color: '#fff',
                px: 3,
                py: 1,
                borderRadius: '4px 4px 0 0',
              }}
            >
              <Typography
                sx={{
                  fontFamily,
                  fontWeight: 600,
                  fontSize: 14,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Discounts and Rebates
              </Typography>
            </Box>
          </Box>

          {/* Discounts/Rebates Rows */}
          <Box sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderTop: 'none' }}>
            {allRows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    sx={{
                      fontFamily,
                      fontSize: 13,
                      color: '#1565c0',
                      fontWeight: 500,
                    }}
                  >
                    {row.description}
                  </Typography>
                  {row.expires && (
                    <Typography
                      sx={{
                        fontFamily,
                        fontSize: 11,
                        color: row.expired ? '#d32f2f' : '#333',
                        fontStyle: 'italic',
                        fontWeight: row.expired ? 600 : 400,
                      }}
                    >
                      ({row.expired ? 'Expired' : 'Expires'}: {row.expires})
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontFamily,
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#1565c0',
                  }}
                >
                  {row.amount}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Total to Pay - always shown if totalPay > 0 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#4caf50',
          color: '#fff',
          px: 2,
          py: 1.5,
          borderRadius: hasDiscountsOrRebates ? 0 : 1,
        }}
      >
        <Typography
          sx={{
            fontFamily,
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          Total Contract Price:
        </Typography>
        <Typography
          sx={{
            fontFamily,
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          {formatCurrency(totalPay)}
        </Typography>
      </Box>

      {/* Financing Options if present */}
      {financingOptions && (
        <Box sx={{ px: 2, py: 1.5, backgroundColor: '#e8f4f8', mt: 1, borderRadius: 1 }}>
          <Typography
            sx={{
              fontFamily,
              fontSize: 12,
              color: '#0d3c61',
            }}
          >
            {financingOptions}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProposalViewInvestment;
